/**
 * What to do when Stripe sends someone to the Connect `refresh_url`.
 *
 * Kept out of the component so it can be tested: the repo's Jest runs under the
 * `node` environment with no DOM, so component rendering is not exercised here.
 *
 * Reaching this route means the Account Link expired — they are single-use, so
 * this happens to anyone who takes too long or re-opens an old link. It says
 * nothing about whether payouts work, so the copy never claims either way.
 *
 * Order matters. Signed-out is checked first because minting without a session
 * 401s, and the interceptor clears localStorage and hard-redirects home — the
 * payee would lose their session on top of the setup they were mid-way through.
 * Same precedence `resolveReturnState` uses for the same reason.
 */
export const REFRESH_STATES = {
  /** No session, so a mint would 401. Ask them to sign in (or reopen the app). */
  SIGNED_OUT: "signedOut",
  /** Session present, minting a fresh link. */
  MINTING: "minting",
  /** The attempt resolved without navigating — offer a retry, never a spinner. */
  FAILED: "failed",
};

const COPY = {
  [REFRESH_STATES.SIGNED_OUT]: {
    title: "PAYOUT_LINK_EXPIRED",
    description: "PAYOUT_LINK_EXPIRED_SIGNED_OUT_DESC",
  },
  [REFRESH_STATES.MINTING]: {
    title: "PAYOUT_LINK_EXPIRED",
    description: "PAYOUT_LINK_EXPIRED_DESC",
  },
  [REFRESH_STATES.FAILED]: {
    title: "PAYOUT_LINK_EXPIRED_FAILED",
    description: "PAYOUT_LINK_EXPIRED_FAILED_DESC",
  },
};

/**
 * The state to render.
 *
 * `mintFailed` covers both ways `startOnboarding` can resolve without
 * navigating: the account has no email on file (Stripe will not open an account
 * without one), or the request errored and `createOnboardingLink` returned
 * undefined. Either leaves the page with nowhere to go, so it has to be a state
 * rather than an assumption that a redirect always follows.
 */
export const resolveRefreshState = ({ signedIn, mintFailed = false }) => {
  if (!signedIn) {
    return REFRESH_STATES.SIGNED_OUT;
  }
  return mintFailed ? REFRESH_STATES.FAILED : REFRESH_STATES.MINTING;
};

/** i18n keys for a state. */
export const getRefreshCopy = (state) => COPY[state];

/** Only mint with a session, and only while nothing has failed yet. */
export const shouldMintLink = (state) => state === REFRESH_STATES.MINTING;

/**
 * Whether a mint attempt left the payee with nowhere to go.
 *
 * Shared by every path that can start one — the mount effect, the retry button
 * and the email modal — so they cannot drift apart. They did: `mint` was fixed
 * to stop treating a successful redirect as failure, while dismissing the modal
 * and a post-email mint failure both kept the page on a spinner forever.
 *
 * `navigated` means the browser is on its way to Stripe. `emailRequired` means
 * the modal has taken over and will call back. Anything else is a dead end and
 * needs the retry.
 */
export const isMintFailure = (result) =>
  !result?.navigated && !result?.emailRequired;
